"use client";

import React from "react";
import Link from "next/link";
import { FolderSearch, ArrowRight, RotateCcw } from "lucide-react";
import { Button } from "./Button";

interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description: string;
  actionLabel?: string;
  actionHref?: string;
  onAction?: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  compact?: boolean;
  className?: string;
}

export function EmptyState({
  icon,
  title,
  description,
  actionLabel,
  actionHref,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  compact = false,
  className = "",
}: EmptyStateProps) {
  return (
    <div
      className={`flex flex-col items-center justify-center text-center rounded-2xl border border-slate-200/80 dark:border-slate-800/80 bg-white/40 dark:bg-slate-900/40 backdrop-blur-sm ${
        compact ? "p-6" : "py-16 px-6"
      } ${className}`}
    >
      {/* Icon with glowing aura */}
      <div className="relative mb-4">
        <div className="absolute inset-0 bg-blue-500/20 dark:bg-cyan-500/20 rounded-full blur-xl scale-150 pointer-events-none" />
        <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 border border-slate-300/60 dark:border-slate-700/60 flex items-center justify-center text-blue-600 dark:text-cyan-400 shadow-lg">
          {icon || <FolderSearch className="w-7 h-7" />}
        </div>
      </div>

      <h3 className="text-base sm:text-lg font-heading font-bold text-slate-900 dark:text-slate-100">
        {title}
      </h3>
      <p className="mt-1 text-xs sm:text-sm text-slate-500 dark:text-slate-400 max-w-sm leading-relaxed">
        {description}
      </p>

      {/* Action Buttons */}
      {(actionLabel || secondaryActionLabel) && (
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          {actionLabel && (
            actionHref ? (
              <Link href={actionHref}>
                <Button size="sm" variant="gradient" className="flex items-center gap-1.5">
                  <span>{actionLabel}</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              </Link>
            ) : (
              <Button size="sm" variant="gradient" onClick={onAction} className="flex items-center gap-1.5">
                <span>{actionLabel}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            )
          )}

          {secondaryActionLabel && (
            <Button
              size="sm"
              variant="outline"
              onClick={onSecondaryAction}
              className="flex items-center gap-1.5 text-xs"
            >
              <RotateCcw className="w-3 h-3" />
              <span>{secondaryActionLabel}</span>
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
